import { useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { useApp } from '../App';
import { PageHeader } from '../components/PageHeader';
import { Avatar, refreshAvatars } from '../components/Avatar';
import { AvatarCropper } from './AvatarCropper';
import { useConfirm } from '../components/Confirm';
import { forgetPinLogin } from '../lib/pin';
import { setTheme, Theme, themeOf } from '../lib/theme';
import { NotificationsCard } from '../chat/NotificationsCard';
import { CalendarAccess } from '../calendar/CalendarAccess';
import { IconChevronRight, IconExit, IconGear } from '../components/icons';
import { positionsLabel } from '../lib/positionsLabel';

/*
 * Личный кабинет: фотография, никнейм, пароль, дата рождения и оформление.
 * Всё, что человек меняет о себе сам; должность и доступы — за
 * администратором.
 */

const THEMES: [Theme, string][] = [
  ['system', 'Как в системе'],
  ['light', 'Светлая'],
  ['dark', 'Тёмная'],
];

export function Profile() {
  const app = useApp();
  const ask = useConfirm();
  const user = app.user;

  const [login, setLogin] = useState(user.login);
  const [birthday, setBirthday] = useState(user.birthday ?? '');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [theme, pickTheme] = useState<Theme>(themeOf);
  const fileRef = useRef<HTMLInputElement>(null);

  const name = `${user.firstName} ${user.lastName}`.trim() || user.login;
  const position = positionsLabel(app.positions);

  const flash = (text: string) => {
    setErr('');
    setMsg(text);
    setTimeout(() => setMsg(''), 2500);
  };

  const saveLogin = async () => {
    if (login.trim() === user.login) return;
    const ok = await ask({
      title: 'Сменить никнейм?',
      text: 'Прежний никнейм освободится. Пин-код придётся задать заново — он привязан к имени входа.',
      okText: 'Сменить',
    });
    if (!ok) return;
    try {
      const r = await api.changeLogin(login.trim());
      if (r.pinReset) forgetPinLogin();
      await app.refreshProfile();
      flash(r.pinReset ? 'Никнейм изменён, пин-код сброшен' : 'Никнейм изменён');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось сменить никнейм');
    }
  };

  const savePassword = async () => {
    if (next !== repeat) {
      setErr('Новые пароли не совпадают');
      return;
    }
    try {
      const r = await api.changePassword(current, next);
      // пин-код сброшен вместе с паролем (спека 2026-10-02-tabs-devices-sync, раздел 4)
      if (r.pinReset) forgetPinLogin();
      setCurrent('');
      setNext('');
      setRepeat('');
      await app.refreshProfile();
      flash(r.pinReset ? 'Пароль изменён, пин-код сброшен' : 'Пароль изменён');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось сменить пароль');
    }
  };

  const saveBirthday = async () => {
    try {
      await api.setBirthday(birthday || null);
      await app.refreshProfile();
      flash('Дата рождения сохранена');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось сохранить дату');
    }
  };

  const removePhoto = async () => {
    const ok = await ask({ title: 'Убрать фотографию?', okText: 'Убрать', danger: true });
    if (!ok) return;
    await api.clearAvatar();
    refreshAvatars();
    await app.refreshProfile();
    flash('Фотография убрана');
  };

  return (
    <div className="view on">
      <PageHeader onBack={() => app.showView('home')} title="Личный кабинет" />
      {app.user.isAdmin && (
        <button className="prow m-only" onClick={() => app.showView('admin')}>
          <IconGear />
          Администрирование
          <span className="prow-arrow"><IconChevronRight /></span>
        </button>
      )}

      <div className="prof">
        <section className="card prof-photo">
          <Avatar login={user.login} name={name} size={96} className="prof-ava" />
          <div className="prof-photo-side">
            <h2>{name}</h2>
            <p className="sub">{position || 'Должность не назначена'}</p>
            {user.email && <p className="sub mute">{user.email}</p>}
            <div className="acts">
              <button className="btn primary" onClick={() => fileRef.current?.click()}>
                {user.avatarAt ? 'Заменить фотографию' : 'Загрузить фотографию'}
              </button>
              {user.avatarAt && (
                <button className="btn outline" onClick={removePhoto}>
                  Убрать
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const picked = e.target.files?.[0];
                  if (picked) setFile(picked);
                  e.target.value = '';
                }}
              />
            </div>
          </div>
        </section>

        <section className="card form">
          <h2>Вход в систему</h2>
          <div className="field">
            <label htmlFor="prof-login">Никнейм</label>
            <div className="form-row aside">
              <input id="prof-login" className="input" value={login} onChange={(e) => setLogin(e.target.value)} />
              <button className="btn primary" disabled={login.trim() === user.login} onClick={saveLogin}>
                Сохранить
              </button>
            </div>
            <span className="field-hint">Входить можно и по никнейму, и по рабочей почте.</span>
          </div>
          <div className="field">
            <label htmlFor="prof-cur">Текущий пароль</label>
            <input id="prof-cur" className="input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div className="form-row">
            <div className="field">
              <label htmlFor="prof-next">Новый пароль</label>
              <input id="prof-next" className="input" type="password" value={next} onChange={(e) => setNext(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="prof-rep">Ещё раз</label>
              <input id="prof-rep" className="input" type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
            </div>
          </div>
          <div className="acts">
            <button className="btn primary" disabled={!current || !next} onClick={savePassword}>
              Сменить пароль
            </button>
          </div>
        </section>

        <section className="card form">
          <h2>О себе</h2>
          <div className="field">
            <label htmlFor="prof-bd">Дата рождения</label>
            <div className="form-row aside">
              <input id="prof-bd" className="input" type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
              <button className="btn primary" onClick={saveBirthday}>
                Сохранить
              </button>
            </div>
          </div>
        </section>

        <section className="card form">
          <h2>Оформление</h2>
          <div className="field">
            <span className="field-label">Тема</span>
            <div className="seg" role="group" aria-label="Тема">
              {THEMES.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={theme === value}
                  onClick={() => {
                    setTheme(value);
                    pickTheme(value);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <span className="field-hint">Выбор сохраняется на этом устройстве: на телефоне и на компьютере он свой.</span>
          </div>
        </section>

        <section className="card">
          <h2>Уведомления</h2>
          <NotificationsCard />
        </section>

        <CalendarAccess />

        {msg && <div className="banner ok">{msg}</div>}
        {err && <div className="banner crit">{err}</div>}
        <button className="prow prow-out m-only" onClick={app.logout}>
          <IconExit />
          Выйти
        </button>
      </div>

      {file && (
        <AvatarCropper
          file={file}
          onCancel={() => setFile(null)}
          onReady={async (blob) => {
            setFile(null);
            try {
              await api.uploadAvatar(blob);
              refreshAvatars();
              await app.refreshProfile();
              flash('Фотография сохранена');
            } catch (e) {
              setErr(e instanceof ApiError ? e.message : 'Не удалось сохранить фотографию');
            }
          }}
        />
      )}
    </div>
  );
}
